<script setup lang="ts">
import type { EChartsOption } from 'echarts'
import type { IMonitorNbkrResponse, MonitorNbkrRange } from '~/components/widget/monitor'
import { MONITOR_NBKR_CREDIT_CATEGORIES } from '~/components/widget/monitor'
import { LazyVChart } from '~/components/ui/chart'

const NBKR_RANGES: MonitorNbkrRange[] = ['6m', '1y', '2y', '3y', 'all']

const props = defineProps<{
  product: 'credit' | 'deposit'
  category?: string
}>()

const { t } = useI18n()

const selectedRange = ref<MonitorNbkrRange>('3y')

// NBKR only publishes sectors for a subset of credit types; the rest have no series at all.
const hasNbkrSector = computed(() => {
  if (props.product === 'deposit') return true

  return MONITOR_NBKR_CREDIT_CATEGORIES.some((item) => item === props.category)
})

const requestParams = computed(() => ({
  product: props.product,
  range: selectedRange.value,
  ...(props.product === 'credit' && props.category ? { category: props.category } : {})
}))

const { data: response, status } = useApi<IMonitorNbkrResponse>('/monitor/nbkr-rates', {
  params: requestParams,
  key: computed(
    () => `monitor-nbkr-${props.product}-${props.category ?? 'all'}-${selectedRange.value}`
  ),
  watch: [requestParams]
})

const points = computed(() => response.value?.data?.points ?? [])
const meta = computed(() => response.value?.meta)

const isLoading = computed(() => status.value === 'pending')
const hasPoints = computed(() => points.value.length > 0)

const preliminaryCount = computed(() => points.value.filter((point) => point.preliminary).length)

const rateFormatter = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 2
})

const formatRate = (value: number) => `${rateFormatter.format(value)}%`

// API sends "YYYY-MM"; the axis and tooltip show it as "MM.YYYY".
const formatMonth = (value: string) => {
  const [year, month] = value.split('-')

  return year && month ? `${month}.${year}` : value
}

// Static styling shared by every render of the chart — only `data` fields change
// per range/category switch, so this is built once instead of on every recompute.
const NBKR_CHART_STATIC_OPTION = {
  grid: { left: 8, right: 16, top: 24, bottom: 8, containLabel: true },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    axisTick: { show: false },
    axisLine: { lineStyle: { color: '#e5e7eb' } },
    axisLabel: { fontSize: 11, color: '#6b7280' }
  },
  yAxis: {
    type: 'value',
    scale: true,
    axisLabel: { formatter: '{value}%' },
    splitLine: { lineStyle: { color: '#f3f4f6' } }
  },
  series: [
    {
      type: 'line',
      smooth: true,
      showSymbol: false,
      connectNulls: false,
      lineStyle: { width: 2.5, color: '#2563eb' },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(37, 99, 235, 0.22)' },
            { offset: 1, color: 'rgba(37, 99, 235, 0.02)' }
          ]
        }
      }
    },
    {
      type: 'line',
      smooth: true,
      showSymbol: false,
      connectNulls: true,
      lineStyle: { width: 2.5, color: '#2563eb', type: 'dashed' }
    }
  ]
}

const chartOption = computed<EChartsOption>(() => {
  const months = points.value.map((point) => formatMonth(point.month))
  const confirmed = points.value.map((point) => (point.preliminary ? null : point.rate))

  // The preliminary tail is drawn as a separate dashed series. It repeats the last
  // confirmed point so the two lines join instead of leaving a visible gap.
  const firstPreliminary = points.value.findIndex((point) => point.preliminary)

  const preliminary = points.value.map((point, index) => {
    if (index === firstPreliminary - 1) return point.rate

    return point.preliminary ? point.rate : null
  })

  return {
    ...NBKR_CHART_STATIC_OPTION,
    tooltip: {
      trigger: 'axis',
      formatter: (params: { dataIndex: number }[]) => {
        const dataIndex = params[0]?.dataIndex

        if (dataIndex === undefined) return ''

        const point = points.value[dataIndex]

        if (!point) return ''

        const preliminaryMark = point.preliminary
          ? `<br/><span>${t('monitor.nbkr.preliminary_mark')}</span>`
          : ''

        return `<strong>${formatMonth(point.month)}</strong><br/>${formatRate(point.rate)}${preliminaryMark}`
      }
    },
    xAxis: { ...NBKR_CHART_STATIC_OPTION.xAxis, data: months },
    series: [
      { ...NBKR_CHART_STATIC_OPTION.series[0], data: confirmed },
      { ...NBKR_CHART_STATIC_OPTION.series[1], data: preliminary }
    ]
  }
})
</script>

<template>
  <div v-if="hasNbkrSector && (hasPoints || isLoading)" class="Card">
    <div class="mb-4 flex flex-wrap items-start justify-between gap-4">
      <div class="flex flex-col gap-1">
        <h2 class="text-lg font-semibold">{{ t('monitor.nbkr.title') }}</h2>
        <p class="text-muted-foreground text-sm">
          {{ t('monitor.nbkr.subtitle', { source: meta?.source ?? 'НБКР' }) }}
        </p>
      </div>

      <div class="flex flex-wrap gap-2">
        <Button
          v-for="range in NBKR_RANGES"
          :key="range"
          :variant="selectedRange === range ? 'blue' : 'outline-gray'"
          size="sm"
          @click="selectedRange = range"
        >
          {{ t(`monitor.nbkr.range_${range}`) }}
        </Button>
      </div>
    </div>

    <div v-if="isLoading" class="h-72 w-full animate-pulse rounded-lg bg-gray-100" />

    <template v-else>
      <LazyVChart
        :option="chartOption"
        autoresize
        class="h-72 w-full"
        :update-options="{ notMerge: true }"
      />

      <p v-if="preliminaryCount" class="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
        {{ t('monitor.nbkr.preliminary_note', { count: preliminaryCount }) }}
      </p>

      <div class="text-muted-foreground mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t pt-3 text-xs">
        <span>{{ t('monitor.nbkr.source', { table: meta?.source_table ?? '—' }) }}</span>
        <span>{{ t('monitor.nbkr.points', { count: points.length }) }}</span>
      </div>
    </template>
  </div>
</template>
